import { type ComponentProps } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Label } from './label';
import { cn } from './helpers';
import { inputVariants } from './input';
import { ArrowDown2 } from 'iconsax-react';
import { Checkbox } from './checkbox';

const Filter = ({
  children,
  ...props
}: ComponentProps<typeof DropdownMenu>) => {
  return (
    <DropdownMenu {...props}>
      <div>{children}</div>
    </DropdownMenu>
  );
};

const FilterClearBtn = ({
  children,
  className,
  onClearClick,
  ...props
}: ComponentProps<'button'> & {
  onClearClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  return (
    <button
      onClick={(e) => {
        onClearClick?.(e);
      }}
      className={cn(
        'text-gray-600 cursor-pointer hover:underline text-xs',
        className
      )}
      {...props}
    >
      {children || 'Clear'}
    </button>
  );
};

const FilterLabel = ({ className, ...props }: ComponentProps<typeof Label>) => {
  return (
    <Label
      className={cn('mb-1 text-[13px]', className)}
      {...props}
    />
  );
};

const FilterTrigger = ({
  className,
  children,
  withArrow = true,
  asChild,
  ...props
}: ComponentProps<'button'> & {
  withArrow?: boolean;
  asChild?: boolean;
}) => {
  if (asChild) {
    return (
      <DropdownMenuTrigger
        asChild
        className={className}
        {...props}
      >
        {children}
      </DropdownMenuTrigger>
    );
  }

  return (
    <DropdownMenuTrigger
      className={cn(
        'w-full relative cursor-pointer items-center ',
        inputVariants({ size: 'sm' }),
        'p-1 flex-wrap min-h-[40px] h-fit gap-1',
        className
      )}
      {...props}
    >
      {children}

      {withArrow && (
        <ArrowDown2
          size={16}
          className='text-primary-1000 absolute right-2 top-1/2 -translate-y-1/2'
        />
      )}
    </DropdownMenuTrigger>
  );
};

const FilterValue = ({
  className,
  children,
  ...props
}: ComponentProps<'span'>) => {
  return (
    <span
      className={cn(
        'text-[13px] min-w-0 flex items-center gap-2 bg-primary-100 px-2 py-1 rounded-md',
        className
      )}
      {...props}
    >
      <span className='truncate'>{children}</span>
    </span>
  );
};

const FilterOptionsList = ({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuContent>) => {
  return (
    <DropdownMenuContent
      className={cn(
        'flex flex-col text-start text-sm max-w-[200px] max-h-[300px]',
        className
      )}
      {...props}
    />
  );
};

const FilterPlaceholder = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      className={cn('text-gray-400 ps-2 text-[13px]', className)}
      {...props}
    />
  );
};

const FilterOptionItem = ({
  selected,
  className,
  preventClose = true,
  withCheckbox = true,
  children,
  checkboxClassName,
  checkboxStyles,
  ...props
}: ComponentProps<typeof DropdownMenuItem> & {
  selected?: boolean;
  preventClose?: boolean;
  withCheckbox?: boolean;
  checkboxClassName?: string;
  checkboxStyles?: React.CSSProperties;
}) => {
  return (
    <DropdownMenuItem
      data-selected={selected}
      onSelect={(event) => {
        if (preventClose) {
          event.preventDefault();
        }
      }}
      onClick={(event) => {
        if (preventClose) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      className={cn(
        'flex items-center gap-2 data-[selected=true]:bg-primary-50',
        className
      )}
      {...props}
    >
      {withCheckbox && (
        <Checkbox
          style={checkboxStyles}
          checked={selected}
          className={cn('border-gray-700', checkboxClassName)}
        />
      )}
      {children}
    </DropdownMenuItem>
  );
};

export {
  Filter,
  FilterClearBtn,
  FilterLabel,
  FilterTrigger,
  FilterValue,
  FilterOptionsList,
  FilterPlaceholder,
  FilterOptionItem,
};
